import {
  get,
  post,
  put,
  patch,
  del,
  buildQueryString
} from './client';
import {
  User,
  Package,
  Wallet,
  Transaction,
  Referral,
  Earning,
  Notification,
  PaginatedResponse,
  ApiResponse,
  PaginationParams,
  FilterParams,
  CreateUserInput,
  UpdateUserInput,
  CreateTransactionInput,
  CreateDepositInput,
  CreateWithdrawalInput,
  UserStats,
  SavingsPackage,
  acceptPaymentPayload,
  acceptPaymentResponse,
  PaymentVerificationResponse,
  ICreateInvestmentResponse,
  IUserFinancialResponse,
  ReferralDataResponse,
  IContact,
  WithdrawalStats,
  WithdrawalQueueResponse,
  PackageStats,
  AnalyticsData
} from '../types';


// User API endpoints

const baseURL = import.meta.env.VITE_API_BASE_URL;

export const userApi = {
  // Get current user profile
  // getProfile: (): Promise<ApiResponse<User>> => get(`${baseURL}/me`),

  // Update user profile
  updateProfile2: (data: UpdateUserInput): Promise<ApiResponse<User>> =>
    put(`${baseURL}/me/update`, data),

  // Get user by ID (admin only)
  getById: (id: string): Promise<ApiResponse<User>> => get(`${baseURL}/admin/users/${id}`),

  // Get all users with pagination and filters (admin only)
  getAll: (params?: PaginationParams & FilterParams): Promise<ApiResponse<PaginatedResponse<User>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/admin/users${queryString ? `?${queryString}` : ''}`);
  },

  // Create user (admin only)
  create: (data: CreateUserInput): Promise<ApiResponse<User>> =>
    post('/users', data),

  // Update user (admin only)
  update: (id: string, data: UpdateUserInput): Promise<ApiResponse<User>> =>
    put(`/users/${id}`, data),

  // Delete user (admin only)
  delete: (id: string): Promise<ApiResponse<void>> => del(`/user/${id}`),

  // Get user stats
  getStats: (): Promise<ApiResponse<UserStats>> => get(`${baseURL}/user/stats`),

  // Update KYC data
  updateKyc: (data: any): Promise<ApiResponse<any | FormData>> =>
    put(`${baseURL}/me/kyc`, data),

  contact: (data: IContact): Promise<ApiResponse<any>> =>
    post('/contact', data),
};

// Package API endpoints
export const packageApi = {
  // Get all active packages
  getAll: (): Promise<ApiResponse<SavingsPackage[]>> => get(`${baseURL}/getPackages`),

  // Get package by ID
  getById: (id: string): Promise<ApiResponse<SavingsPackage>> => get(`${baseURL}/packages/${id}`),


  // Delete package (admin only)
  delete: (id: string): Promise<ApiResponse<void>> => del(`${baseURL}/packages/${id}`),

  // Get package statistics (admin only)
  getStats: (id: string): Promise<ApiResponse<{ userCount: number; totalDeposits: number }>> =>
    get(`${baseURL}/packages/${id}/stats`),

  create: (data: any): Promise<ApiResponse<Package>> =>
    post(`${baseURL}/admin/packages`, data),

  // Update existing package
  update: (id: string, data: any): Promise<ApiResponse<Package>> =>
    put(`${baseURL}/admin/packages/${id}`, data),

  // Delete package
  remove: (id: string): Promise<ApiResponse<void>> =>
    del(`${baseURL}/admin/packages/${id}`),

  // Update active status
  updateStatus: (id: string, data: { is_active: boolean }): Promise<ApiResponse<Package>> =>
    patch(`${baseURL}/admin/packages/${id}/status`, data),

  save: (data: any): Promise<ApiResponse<Package>> => {
    const method = data._id ? put : post;
    const url = data._id ? `${baseURL}/admin/packages/${data._id}` : `${baseURL}/admin/packages`;
    return method(url, data);
  }
};

// Wallet API endpoints
export const walletApi = {
  // Get user wallet
  get: (): Promise<ApiResponse<Wallet>> => get(`${baseURL}/wallet`),

  // Get wallet by user ID (admin only)
  getByUserId: (userId: string): Promise<ApiResponse<Wallet>> => get(`${baseURL}/wallet/${userId}`),

  // Get wallet history
  getHistory: (params?: PaginationParams & FilterParams): Promise<ApiResponse<PaginatedResponse<Transaction>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/wallet/history${queryString ? `?${queryString}` : ''}`);
  },


};

// User-specific API endpoints
export const userSpecificApi = {
  // Get user financial info
  getFinancialInfo: (): Promise<ApiResponse<IUserFinancialResponse>> => get(`${baseURL}/user/getInvestmentInfo`),

  // Get user wallet
  getWallet: (): Promise<ApiResponse<any>> => get(`${baseURL}/user/wallet`),

  // Get user transactions with pagination
  getTransactions: (params?: any): Promise<ApiResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/user/transactions${queryString ? `?${queryString}` : ''}`);
  },

  // Export user transactions
  exportTransactions: (params?: any): Promise<Blob> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/user/transactions/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    }) as any;
  },

  // Get user packages
  getPackages: (): Promise<ApiResponse<any>> => get(`${baseURL}/user/packages`),

  // Get user profile
  getProfile: (): Promise<ApiResponse<any>> => get(`${baseURL}/me`),

  // Update user profile
  updateProfile: (data: any): Promise<ApiResponse<any | FormData>> => put(`${baseURL}/me/update`, data),

  // Get user stats
  getStats: (): Promise<ApiResponse<any>> => get(`${baseURL}/user/stats`),

  //add payment details
  updatePaymentDetails: (data: any): Promise<ApiResponse<any>> => put(`${baseURL}/updatePaymentDetails`, data),
};




// Transaction API endpoints
export const transactionApi = {
  // Get user transactions
  getAll: (params?: PaginationParams & FilterParams): Promise<ApiResponse<PaginatedResponse<Transaction>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/transactions${queryString ? `?${queryString}` : ''}`);
  },

  // Get transaction by ID
  getById: (id: string): Promise<ApiResponse<Transaction>> => get(`${baseURL}/transactions/${id}`),

  // Create for savings
  createDeposit: (data: CreateDepositInput): Promise<ApiResponse<ICreateInvestmentResponse>> =>
    post(`${baseURL}/invest`, data),

  // Create withdrawal
  createWithdrawal: (data: CreateWithdrawalInput): Promise<ApiResponse<Transaction>> =>
    post(`${baseURL}/withdraw`, data),

  // Create generic transaction (admin only)
  create: (data: CreateTransactionInput): Promise<ApiResponse<Transaction>> =>
    post(`${baseURL}/transactions`, data),

  // Update transaction status (admin only)
  updateStatus: (id: string, status: Transaction['status']): Promise<ApiResponse<Transaction>> =>
    patch(`${baseURL}/transactions/${id}/status`, { status }),

  // Get all transactions (admin only)
  getAllAdmin: (params?: PaginationParams & FilterParams): Promise<ApiResponse<PaginatedResponse<Transaction>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/admin/transactions${queryString ? `?${queryString}` : ''}`);
  },

  acceptPayment: (data: acceptPaymentPayload): Promise<ApiResponse<acceptPaymentResponse>> => {
    return post(`${baseURL}/acceptPayment`, data)
  },

  verifyPayment: (reference: string): Promise<ApiResponse<PaymentVerificationResponse>> => {
    return post(`${baseURL}/verifyPayment`, reference)
  },

  closePackage: (data: any): Promise<ApiResponse<any>> =>
    post(`/investment/close`, data),
};

// Referral API endpoints
export const referralApi = {
  // Get user referrals
  getAll: (params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Referral>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/referrals${queryString ? `?${queryString}` : ''}`);
  },

  // Get referral by ID
  getById: (id: string): Promise<ApiResponse<Referral>> => get(`${baseURL}/referrals/${id}`),

  // Get referral stats
  getStats: (): Promise<ApiResponse<ReferralDataResponse>> => get(`${baseURL}/referrals/stats`),

  // Create referral (usually automatic)
  create: (referredUserId: string): Promise<ApiResponse<Referral>> =>
    post(`${baseURL}/referrals`, { referredUserId }),

  // Get all referrals (admin only)
  getAllAdmin: (params?: PaginationParams & FilterParams): Promise<ApiResponse<PaginatedResponse<Referral>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/admin/referrals${queryString ? `?${queryString}` : ''}`);
  },
};

// Earnings API endpoints
export const earningApi = {
  // Get user earnings
  getAll: (params?: PaginationParams & FilterParams): Promise<ApiResponse<PaginatedResponse<Earning>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/earnings${queryString ? `?${queryString}` : ''}`);
  },

  // Get earning by ID
  getById: (id: string): Promise<ApiResponse<Earning>> => get(`${baseURL}/earnings/${id}`),

  // Get earnings summary
  getSummary: (): Promise<ApiResponse<{ totalEarnings: number; monthlyEarnings: number; pendingEarnings: number }>> =>
    get(`${baseURL}/earnings/summary`),

  // Create earning (admin only)
  create: (data: Omit<Earning, 'id' | 'creditedDate'>): Promise<ApiResponse<Earning>> =>
    post(`${baseURL}/earnings`, data),

  // Process pending earnings (admin only)
  processPending: (packageId: string): Promise<ApiResponse<{ processed: number }>> =>
    post(`${baseURL}/earnings/process/${packageId}`),
};

// Notification API endpoints
export const notificationApi = {
  // Get user notifications
  getAll: (params?: PaginationParams & { unreadOnly?: boolean }): Promise<ApiResponse<PaginatedResponse<Notification>>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/notifications${queryString ? `?${queryString}` : ''}`);
  },

  // Get notification by ID
  getById: (id: string): Promise<ApiResponse<Notification>> => get(`${baseURL}/notifications/${id}`),

  // Mark notification as read
  markAsRead: (id: string): Promise<ApiResponse<Notification>> =>
    patch(`${baseURL}/notifications/${id}/read`),

  // Mark all notifications as read
  markAllAsRead: (): Promise<ApiResponse<{ updated: number }>> =>
    patch('/notifications/read-all'),

  // Get unread count
  getUnreadCount: (): Promise<ApiResponse<{ count: number }>> =>
    get(`${baseURL}/notifications/unread-count`),

  // Create notification (admin only)
  create: (data: Omit<Notification, 'id' | 'date' | 'read'>): Promise<ApiResponse<Notification>> =>
    post('/notifications', data),

  // Delete notification
  delete: (id: string): Promise<ApiResponse<void>> => del(`${baseURL}/notifications/${id}`),
};

// Admin/Dashboard API endpoints
export const adminApi = {

  getDashboardStats: async (params?: { search?: string; page?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.search) query.append("search", params.search);
    if (params?.page) query.append("page", params.page.toString());
    if (params?.status) query.append("status", params.status);

    return get(`${baseURL}/admin/dashboard/stats?${query.toString()}`);
  },


  // Get recent activities
  getRecentActivities: (limit?: number): Promise<ApiResponse<Transaction[]>> =>
    get(`${baseURL}/admin/dashboard/activities${limit ? `?limit=${limit}` : ''}`),

  // System health check
  getSystemHealth: (): Promise<ApiResponse<{ status: string; uptime: number; memory: any }>> =>
    get(`${baseURL}/admin/system/health`),

  // System Settings endpoints
  getSystemSettings: (): Promise<ApiResponse<any>> => get(`${baseURL}/admin/settings`),
  updateSystemSettings: (settings: any): Promise<ApiResponse<any>> => put(`${baseURL}/admin/settings`, settings),

  getDeposits: (params?: any): Promise<ApiResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/admin/deposits${queryString ? `?${queryString}` : ''}`);
  },

  getTransactions: (params?: any): Promise<ApiResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`${baseURL}/admin/transactions${queryString ? `?${queryString}` : ''}`);
  },

  exportTransactions: (params?: any): Promise<Blob> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(
      `${baseURL}/admin/transactions/export${queryString ? `?${queryString}` : ''}`,
      { responseType: 'blob' }
    ) as any;
  },

  getAnalytics: (dateRange?: string): Promise<ApiResponse<AnalyticsData>> => {
    const rangeParam = dateRange ? `?range=${dateRange}` : '';
    return get(`${baseURL}/admin/analytics${rangeParam}`);
  },

  exportAnalytics: (dateRange?: string): Promise<Blob> => {
    const rangeParam = dateRange ? `?range=${dateRange}` : '';
    return get(`${baseURL}/admin/analytics${rangeParam}`,
      { responseType: 'blob' }
    ) as any;
  },

  updateDepositStatus: (id: string, data: any): Promise<ApiResponse<any>> =>
    post(`${baseURL}/admin/deposits/${id}/status`, data),

  exportDeposits: (): Promise<Blob> =>
    get(`${baseURL}/admin/deposits/export`, { responseType: 'blob' }) as any,

  verifyKYC: (status: string, userId: string): Promise<ApiResponse<any>> =>
    post(`${baseURL}/admin/verifyUser/ `, { status, userId }),

  getWithdrawalRequests: (params?: any): Promise<ApiResponse<WithdrawalQueueResponse>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`/admin/withdrawals${queryString ? `?${queryString}` : ''} `);
  },

  getWithdrawalStats: (): Promise<ApiResponse<WithdrawalStats>> => get('/admin/withdrawals/stats'),

  processWithdrawal: (data: any): Promise<ApiResponse<any>> =>
    post(`/admin/processWithdrawals`, data),

  exportWithdrawals: (params?: any): Promise<Blob> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`/admin/withdrawals/export${queryString ? `?${queryString}` : ''} `, {
      responseType: 'blob'
    }) as any;
  },

  getPackageStats: (): Promise<ApiResponse<Record<string, PackageStats>>> => get(`${baseURL}/admin/packageStats`),


  getSMS: (params?: any): Promise<ApiResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    return get(`admin/contact/all${queryString ? `?${queryString}` : ''} `);
  },
};

// Auth API endpoints
export const authApi = {
  // Login with email/password
  login: (email: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> =>
    post(`${baseURL}/auth/login`, { email, password }),

  // Register new user
  register: (data: CreateUserInput): Promise<ApiResponse<{ user: User; token: string }>> =>
    post(`${baseURL}/auth/register`, data),

  // Logout
  logout: (): Promise<ApiResponse<void>> => get(`${baseURL}/auth/logout`),

  // Refresh token
  refreshToken: (): Promise<ApiResponse<{ token: string }>> =>
    post(`${baseURL}/auth/refresh`),

  // Forgot password
  forgotPassword: (email: string): Promise<ApiResponse<{ message: string }>> =>
    post(`${baseURL}/auth/password/forgot-password`, { email }),

  // Reset password
  resetPassword: (token: string, password: string): Promise<ApiResponse<{ message: string }>> =>
    put(`${baseURL}/auth/password/reset-password`, { token, password }),

  // Verify email
  verifyEmail: (token: string): Promise<ApiResponse<{ message: string }>> =>
    post('/auth/verify-email', { token }),

  //update password
  updatePassword: (data: any): Promise<ApiResponse<{ user: User; token: string }>> =>
    put(`${baseURL}/auth/password/update`, data),

  googleLogin: (credential: string, referralCode?: string): Promise<ApiResponse<{ user: User; token: string }>> =>
    post(`${baseURL}/auth/google`, { credential, referralCode }),


};



// Export all APIs
export const api = {
  user: { ...userApi, ...userSpecificApi },
  package: packageApi,
  wallet: walletApi,
  transaction: transactionApi,
  referral: referralApi,
  earning: earningApi,
  notification: notificationApi,
  admin: adminApi,
  auth: authApi
};

export const endpointURL = baseURL;

export default api;
